import React, { useCallback, useEffect, useState } from "react"
import { RouteComponentProps, useParams } from "react-router"
import styles from "./ProjectMenuSwitch.module.scss"
import { useAppDispatch, useAppSelector } from "../../store/hooks"
import { definedMenuItems } from "../../utility/menu/definedMenuItems"
import loadable from "@loadable/component"
import FullscreenLoader from "../FullscreenLoader/FullscreenLoader"
import { MenuItem } from "../../models/menu"
import { buildArticleActionUrl } from "../../utility/knowledgeBase/articleUrlHelper"
import { ArticleAction } from "../../models/article"
import { push } from "connected-react-router"
import OldWorkplaceFrame from "../OldWorkplaceFrame/OldWorkplaceFrame"
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary"
import { promiseDelay } from "../../utility/common/promiseDelay"
import { selectCurrentMainMenu } from "../../store/menu/selectors"
import { selectMainMenu } from "../../store/menu/thunks"
import { buildMenuItemUrl } from "../../utility/menu/menuUrlHelper"
import { MatchPath } from "../../types/matchPath"
import UserManagement from "../../pages/UserManagement/UserManagement"
import UserPage from "../../pages/User/User"
import { isWorkplaceIdOld } from "../../utility/project/frameHelper"
import { selectReferences } from "../../store/projects/selectors"
import { getQueueCategoriesForReroute } from "../../store/queues/thunks"
import { useGetCustomMenusQuery } from "../../api/controllers/dialogs"
import { skipToken } from "@reduxjs/toolkit/query"
import CustomMenu from "../CustomMenu/CustomMenu"

const Loading = <FullscreenLoader hideLogo />
const Dialogs = loadable(() => promiseDelay(import("../../pages/Dialogs/Dialogs"), 200), { fallback: Loading })
const Queues = loadable(() => promiseDelay(import("../../pages/Queues/Queues"), 200), { fallback: Loading })
const QueuesV2 = loadable(() => promiseDelay(import("../../pages/QueuesV2/QueuesV2"), 200), { fallback: Loading })
const Licence = loadable(() => promiseDelay(import("../../pages/License/LicensePage"), 200), { fallback: Loading })
const MassMailing = loadable(() => promiseDelay(import("../../pages/MassMailing/MassMailing"), 200), {
    fallback: Loading
})
const Workshop = loadable(() => promiseDelay(import("../../pages/Workshop/Workshop"), 200), {
    fallback: Loading
})
const KnowledgeBase = loadable(() => promiseDelay(import("../../pages/KnowledgeBase/KnowledgeBase"), 200), {
    fallback: Loading
})
const ProjectSettings = loadable(() => promiseDelay(import("../../pages/ProjectSettings/ProjectSettings"), 200), {
    fallback: Loading
})

export interface ProjectMenuSwitchProps extends RouteComponentProps<MatchPath> {
    url: string
    defaultArticleId: string | boolean
    defaultMenu?: MenuItem
    onCheckMenuAvailability: (menuId: string) => boolean
    projectId?: string
}

const ProjectMenuSwitch: React.FC<ProjectMenuSwitchProps> = props => {
    const { defaultArticleId, url, defaultMenu, onCheckMenuAvailability, projectId } = props
    const selectedMenu = useAppSelector(selectCurrentMainMenu)
    const dispatch = useAppDispatch()
    const { menuId } = useParams<MatchPath>()
    const [enableOldWorkplace, setEnableOldWorkplace] = useState<boolean>(true)
    const refs = useAppSelector(selectReferences)
    const getCustomMenusQuery = useGetCustomMenusQuery(projectId ?? skipToken)
    const customMenus = getCustomMenusQuery?.data?.Values

    const handleRouteToDefault = useCallback(() => {
        if (typeof defaultArticleId === "string") {
            const routePath = buildArticleActionUrl(
                `${url}/${definedMenuItems.KnowledgeBase.id}`,
                ArticleAction.View,
                defaultArticleId
            )
            dispatch(push(routePath))
        } else if (defaultMenu) {
            const routePath = buildMenuItemUrl(url, defaultMenu.id)
            dispatch(push(routePath))
        }
    }, [defaultArticleId, defaultMenu, dispatch, url])

    useEffect(() => {
        if (!selectedMenu && !menuId) {
            handleRouteToDefault()
        } else if (menuId) {
            if (onCheckMenuAvailability(menuId)) {
                dispatch(selectMainMenu(menuId))
            } else if (refs) {
                dispatch(push(""))
            }
        }
    }, [dispatch, handleRouteToDefault, menuId, onCheckMenuAvailability, selectedMenu, refs])

    useEffect(() => {
        if (!projectId) return
        dispatch(getQueueCategoriesForReroute(projectId))
    }, [projectId])

    const renderMenus = () => {
        switch (menuId) {
            case definedMenuItems.Dialogs.id:
                return <Dialogs />
            case definedMenuItems.Queues.id:
                return <Queues />
            case definedMenuItems.QueuesV2.id:
                return <QueuesV2 />
            case definedMenuItems.Profile.id:
                return <UserPage />
            case definedMenuItems.Settings.id:
                return <ProjectSettings />
            case definedMenuItems.UserManagement.id:
                return <UserManagement />
            case definedMenuItems.KnowledgeBase.id:
                return <KnowledgeBase />
            case definedMenuItems.MassMailing.id:
                return <MassMailing />
            case definedMenuItems.Workshop.id:
                return <Workshop />
            case definedMenuItems.License.id:
                return <Licence />
            default:
                return null
        }
    }

    useEffect(() => {
        const isEnabled = (menuId && isWorkplaceIdOld(menuId)) || false

        setEnableOldWorkplace(isEnabled)
    }, [menuId])

    return (
        <div className={styles.projectMenuSwitch}>
            <ErrorBoundary hideLogo>
                {customMenus &&
                    customMenus.map(menu => (
                        <CustomMenu
                            url={menu.Url}
                            frameName={menu.Title}
                            menuId={menu.Id}
                            projectId={projectId ?? ""}
                            key={menu.Id}
                        />
                    ))}
                <OldWorkplaceFrame selectedMenu={selectedMenu} menuId={menuId} isActive={enableOldWorkplace} />
                {renderMenus()}
            </ErrorBoundary>
        </div>
    )
}

export default ProjectMenuSwitch
